import { useTranslation } from 'react-i18next';
import { formatGetal, isGetal, isTelling } from '../format';
import { GeenMeting } from './MeasurementValue';
import { heeftEigenNaam, PADREGEL_REM, veldnaamVan } from '../veldnaam';
import type { Regressierij, Veldnamen } from '../work-quality.api';

export interface RegressieBlokProps {
  regressies: Regressierij[];
  veldnamen?: Veldnamen;
}

/** Het woord per soort: de soort draagt de betekenis, het icoon is versiering. */
const SOORT: Record<Regressierij['soort'], { woord: string; icoon: string }> = {
  aas_gedaald: { woord: 'qa.ai.regressie.aasGedaald', icoon: 'trending_down' },
  meer_correcties: { woord: 'qa.ai.regressie.meerCorrecties', icoon: 'edit_note' },
};

/**
 * De Regressielijst "Wat is slechter geworden" (EXPERIENCE.md S6): één lijst in de volgorde van de
 * dienst, per regel de soort als woord, het onderdeel of veld, en nu tegenover vorige week.
 *
 * De soort komt uit de dienst: zij is een drempeltoets over getallen die dit scherm afdrukt
 * (AD-19). Er wordt niets afgekapt en niets hergroepeerd; de volgorde is die van het rapport.
 */
export function RegressieBlok({ regressies, veldnamen }: RegressieBlokProps) {
  const { t } = useTranslation();

  return (
    <section className="mb-4" data-testid="werkkwaliteit-regressielijst">
      <h3 className="mb-2 text-sm font-semibold text-[var(--foreground-heading)]">{t('qa.ai.regressie.kop')}</h3>
      {regressies.length === 0 ? (
        <p className="text-[0.8125rem] text-[var(--foreground-muted)]" data-testid="werkkwaliteit-regressies-geen">
          {t('qa.ai.regressie.geen')}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--border-soft)] rounded-lg border border-[var(--border-soft)]">
          {/* De index in de sleutel: het type staat een rij toe zonder `veld` en zonder `categorie`,
              en twee van die rijen zouden anders dezelfde sleutel krijgen. */}
          {/* Een onbekende soort komt ongecontroleerd uit de dienst; zonder woord valt hij weg in plaats
              van het hele tabblad te laten omvallen. */}
          {regressies.map((rij, index) =>
            Object.hasOwn(SOORT, rij.soort) ? <Rij key={`${naamVan(rij)}-${index}`} rij={rij} veldnamen={veldnamen} /> : null,
          )}
        </ul>
      )}
    </section>
  );
}

/** Waarop deze rij te herkennen is: het veldpad, anders de onderdeelnaam. */
function naamVan(rij: Regressierij): string {
  return rij.veld ?? rij.categorie ?? '';
}

function Rij({ rij, veldnamen }: { rij: Regressierij; veldnamen?: Veldnamen }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const soort = SOORT[rij.soort];

  // Een onderdeelnaam wordt niet in de veldnamentabel opgezocht: die tabel gaat over XML-paden. Een
  // rij zonder allebei blijft staan en zegt dat de naam onbekend is.
  const naam = naamVan(rij);
  const label =
    rij.veld === undefined ? (naam === '' ? t('qa.eigen.voorbeeldOnbekend') : naam) : veldnaamVan(rij.veld, veldnamen);

  return (
    <li className="flex gap-2 px-3 py-2 text-[0.8125rem]" data-testid={`werkkwaliteit-regressie-${naam}`}>
      <span
        aria-hidden="true"
        className="material-symbols-outlined w-[1.143rem] shrink-0 overflow-hidden text-[1.143rem] leading-5 text-[var(--warning-text)]"
        data-werkkwaliteit-icoon={rij.soort}
      >
        {soort.icoon}
      </span>
      <span className="min-w-0">
        <span className="block font-semibold text-[var(--foreground)]">{t(soort.woord)}</span>
        <span className="block font-medium text-[var(--foreground)] [overflow-wrap:anywhere]">{label}</span>
        {/* Het pad blijft staan voor wie het veld technisch wil nazoeken, als het niet al de naam ís. */}
        {rij.veld !== undefined && heeftEigenNaam(rij.veld, veldnamen) ? <span className={PADREGEL_REM}>{rij.veld}</span> : null}
        <span className="mt-0.5 block text-[var(--foreground-muted)]">
          {rij.soort === 'aas_gedaald' ? (
            isGetal(rij.waarde) ? (
              <>
                {isGetal(rij.vorige)
                  ? t('qa.ai.regressie.aas', { waarde: formatGetal(rij.waarde, locale), vorige: formatGetal(rij.vorige, locale) })
                  : t('qa.ai.regressie.aasZonderVorige', { waarde: formatGetal(rij.waarde, locale) })}
                {isGetal(rij.verschilPp) && rij.verschilPp < 0 ? (
                  <span className="text-[var(--warning-text)]">
                    {' '}
                    {t('qa.ai.regressie.slechter', {
                      // Het teken staat al in het woord "slechter".
                      count: Math.abs(rij.verschilPp),
                      verschil: formatGetal(Math.abs(rij.verschilPp), locale),
                    })}
                  </span>
                ) : null}
              </>
            ) : (
              <GeenMeting label={t('qa.alle.kerngetalNietGemeten')} />
            )
          ) : // `isTelling` en niet blind afdrukken: `formatGetal(null)` levert "0", en een nul uit
          // afwezigheid leest hier als "nul aanpassingen".
          isTelling(rij.correcties) && isTelling(rij.correctiesVorige) ? (
            t('qa.ai.regressie.correcties', {
              count: rij.correcties,
              aantal: formatGetal(rij.correcties, locale),
              vorige: formatGetal(rij.correctiesVorige, locale),
            })
          ) : (
            <GeenMeting label={t('qa.alle.kerngetalNietGemeten')} />
          )}
        </span>
      </span>
    </li>
  );
}
